import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Clock,
  Club,
  Crown,
  Diamond,
  Gift,
  HelpCircle,
  Radio,
  ShieldCheck,
  Sparkles,
  Ticket,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { GAMES, GAME_CATEGORIES } from '../../config/games';
import { useHistory } from '../../store/history';

const CATEGORY_ICONS: Record<string, typeof Sparkles> = {
  sparkles: Sparkles,
  diamond: Diamond,
  zap: Zap,
  radio: Radio,
  club: Club,
  ticket: Ticket,
};

export function Sidebar() {
  const entries = useHistory((s) => s.entries);
  const recentIds = useMemo(
    () => Array.from(new Set(entries.slice(0, 4).map((r) => r.gameId))).slice(0, 3),
    [entries],
  );

  return (
    <aside className="sticky top-16 hidden h-[calc(100dvh-4rem)] w-60 shrink-0 overflow-y-auto border-r border-white/8 bg-ink-1000/50 px-3 py-4 xl:block">
      <nav aria-label="Game categories" className="space-y-1">
        <NavLink
          to="/casino"
          end
          className={({ isActive }) =>
            cn(
              'flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
              isActive
                ? 'bg-agave-500/15 text-white'
                : 'text-ink-200 hover:bg-white/6 hover:text-white',
            )
          }
        >
          <span className="flex items-center gap-3">
            <TrendingUp className="size-4 text-agave-400" />
            All games
          </span>
          <span className="text-xs text-ink-400">{GAMES.length}</span>
        </NavLink>

        {GAME_CATEGORIES.map((category) => {
          const Icon = CATEGORY_ICONS[category.icon] ?? Sparkles;
          const count = GAMES.filter((g) => g.category === category.id).length;
          return (
            <NavLink
              key={category.id}
              to={`/casino/${category.id}`}
              className={({ isActive }) =>
                cn(
                  'flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-agave-500/15 text-white'
                    : 'text-ink-200 hover:bg-white/6 hover:text-white',
                )
              }
            >
              <span className="flex items-center gap-3">
                <Icon className="size-4 text-ink-400" />
                {category.label}
              </span>
              <span className="text-xs text-ink-400">{count}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="my-4 h-px bg-white/8" />

      <div className="space-y-1">
        {[
          { to: '/promotions', label: 'Promotions', icon: Gift, tone: 'text-gold-400' },
          { to: '/vip', label: 'VIP Club', icon: Crown, tone: 'text-gold-400' },
          {
            to: '/responsible-gaming',
            label: 'Responsible gaming',
            icon: ShieldCheck,
            tone: 'text-agave-400',
          },
          { to: '/help', label: 'Help centre', icon: HelpCircle, tone: 'text-sky-450' },
        ].map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-white/8 text-white'
                  : 'text-ink-200 hover:bg-white/6 hover:text-white',
              )
            }
          >
            <item.icon className={cn('size-4', item.tone)} />
            {item.label}
          </NavLink>
        ))}
      </div>

      {recentIds.length > 0 ? (
        <>
          <div className="my-4 h-px bg-white/8" />
          <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            Recently played
          </p>
          <div className="space-y-1">
            {recentIds.map((id) => {
              const game = GAMES.find((g) => g.id === id);
              if (!game) return null;
              return (
                <NavLink
                  key={id}
                  to={`/games/${game.id}`}
                  className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-ink-300 transition-colors hover:bg-white/6 hover:text-white"
                >
                  <span
                    className="grid size-7 shrink-0 place-items-center rounded-lg text-sm"
                    style={{
                      backgroundImage: `linear-gradient(135deg, ${game.art.from}, ${game.art.to})`,
                    }}
                  >
                    {game.art.glyph}
                  </span>
                  <span className="truncate">{game.name}</span>
                </NavLink>
              );
            })}
          </div>
        </>
      ) : null}

      <div className="mt-6 rounded-xl border border-white/8 bg-white/4 p-3">
        <p className="flex items-center gap-2 text-xs font-semibold text-white">
          <Clock className="size-3.5 text-agave-400" /> Play for fun
        </p>
        <p className="mt-1 text-[11px] leading-relaxed text-ink-400">
          Agave uses virtual credits only. No real-money wagering, no payouts.
        </p>
      </div>
    </aside>
  );
}
